import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface BillingReadinessData {
  isReady: boolean;
  monthlyRevenue: number;
  activeSubscriptions: number;
  enrolledStudents: number;
  estimatedYearlySavings: number;
  estimatedYearlyRevenueIncrease: number;
  processingFeeRate: number; // 3.5% default
}

/**
 * Hook to calculate billing readiness and savings estimates
 * Determines if company is ready to enable billing based on:
 * - Completed onboarding tasks
 * - Minimum students onboarded (10+)
 * - Active subscriptions with revenue
 * - Students enrolled in subscriptions
 */
export function useBillingReadiness() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();

  return useQuery<BillingReadinessData>({
    queryKey: queryKeys.dashboard.billingReadiness(companyId),
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once to avoid spamming errors
    queryFn: async () => {
      if (!companyId) {
        return {
          isReady: false,
          monthlyRevenue: 0,
          activeSubscriptions: 0,
          enrolledStudents: 0,
          estimatedYearlySavings: 0,
          estimatedYearlyRevenueIncrease: 0,
          processingFeeRate: 3.5,
        };
      }

      const PROCESSING_FEE_RATE = 3.5; // 3.5% industry average

      // Get active subscription enrollments with prices
      // Note: price is stored in company_subscriptions, not subscription_enrollments
      const { data: enrollments, error: enrollmentsError } = await (supabase as any)
        .from('subscription_enrollments')
        .select(`
          id,
          user_id,
          child_id,
          subscription_id,
          company_subscriptions!subscription_enrollments_subscription_id_fkey (
            id,
            billing_cycle,
            price
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (enrollmentsError) {
        console.error('[useBillingReadiness] Error fetching enrollments:', enrollmentsError);
        return {
          isReady: false,
          monthlyRevenue: 0,
          activeSubscriptions: 0,
          enrolledStudents: 0,
          estimatedYearlySavings: 0,
          estimatedYearlyRevenueIncrease: 0,
          processingFeeRate: PROCESSING_FEE_RATE,
        };
      }

      const activeEnrollments = enrollments || [];
      const activeSubscriptions = activeEnrollments.length;

      // Calculate monthly revenue (convert all to monthly)
      let monthlyRevenue = 0;
      activeEnrollments.forEach((enrollment: any) => {
        const price = Number(enrollment.price || enrollment.company_subscriptions?.price || 0);
        const billingCycle = enrollment.company_subscriptions?.billing_cycle || 'monthly';
        
        let monthlyPrice = 0;
        if (billingCycle === 'monthly') {
          monthlyPrice = price;
        } else if (billingCycle === 'quarterly') {
          monthlyPrice = price / 3;
        } else if (billingCycle === 'annual') {
          monthlyPrice = price / 12;
        } else {
          monthlyPrice = price; // Default to monthly
        }
        
        monthlyRevenue += monthlyPrice;
      });

      // Count unique enrolled students (users or children)
      const enrolledStudentIds = new Set<string>();
      activeEnrollments.forEach((enrollment: any) => {
        if (enrollment.user_id) {
          enrolledStudentIds.add(`user_${enrollment.user_id}`);
        }
        if (enrollment.child_id) {
          enrolledStudentIds.add(`child_${enrollment.child_id}`);
        }
      });
      const enrolledStudents = enrolledStudentIds.size;

      // Calculate estimated yearly savings and revenue increase
      // Savings = processing fees saved (3.5% of revenue) over 12 months
      // Revenue increase = additional revenue from zero-fee processing over 12 months
      const yearlyRevenue = monthlyRevenue * 12;
      const currentProcessingFees = yearlyRevenue * (PROCESSING_FEE_RATE / 100);
      const estimatedYearlySavings = currentProcessingFees; // They save all processing fees
      const estimatedYearlyRevenueIncrease = currentProcessingFees; // Same amount, different framing

      // Determine if ready: minimum 10 students enrolled, active subscriptions with revenue
      const isReady = enrolledStudents >= 10 && activeSubscriptions > 0 && monthlyRevenue > 0;

      return {
        isReady,
        monthlyRevenue,
        activeSubscriptions,
        enrolledStudents,
        estimatedYearlySavings,
        estimatedYearlyRevenueIncrease,
        processingFeeRate: PROCESSING_FEE_RATE,
      };
    },
  });
}
